import { AnimatePresence, motion } from "motion/react";
import { Check, CalendarDays, ChevronDown, Plus, Search, SquareKanban, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../components/ui/popover";
import { readRemembered, writeRemembered } from "../../../../shared/lib/remembered";
import { useSafeSearchParams } from "../../lib/urlState";
import { Button } from "../../components/ui/button";
import { CountPill, Segmented, Ticker } from "../../components/ui/primitives";
import { DEPTS, editorsOf, isMyStaging, ME_CLIENT, PEOPLE, PROJECTS, type Dept } from "../../data/people";
import { needsCheck } from "../../lib/domain";
import { easeOut, spring } from "../../lib/motion";
import { dayOf } from "../../lib/time";
import { useNow } from "../../lib/useNow";
import { cn } from "../../lib/utils";
import { useStore } from "../../store";
import type { Status, Task } from "../../types";
import { DayByPeople } from "./calendars/DayByPeople";
import { TaskCard } from "./TaskCard";
import { PushPrompt } from "../../components/PushPrompt";

const COLS: { status: Status; name: string; dot: string }[] = [
  // Темы без исполнителя: их берёт монтажёр группы или назначает постановщик.
  { status: "open", name: "Свободные", dot: "bg-muted-foreground" },
  { status: "wait", name: "Ожидает", dot: "bg-wait" },
  { status: "work", name: "В работе", dot: "bg-work" },
  { status: "review", name: "Проверка", dot: "bg-review" },
  { status: "rework", name: "Доработка", dot: "bg-rework" },
  // Ролики с обеими галочками, у которых нет ссылки на вышедший пост. Своя
  // колонка, а не хвост «Выполнено»: их и искали руками в конце месяца.
  { status: "publish", name: "Публикация", dot: "bg-publish" },
  { status: "done", name: "Выполнено", dot: "bg-done" },
];

/** Когда по задаче в последний раз что-то сдали. */
const lastSubmitAt = (t: Task) =>
  Math.max(0, ...t.items.flatMap((i) => i.versions.map((v) => v.at)));

/** Самый давний аппрув без ссылки. */
const oldestApprovedAt = (t: Task) => {
  const waiting = t.items.filter((i) => i.status === "approved").map((i) => i.approvedAt ?? 0);
  return waiting.length ? Math.min(...waiting) : Number.MAX_SAFE_INTEGER;
};

/**
 * Когда тема вышла: последняя ссылка на пост или аппрув (09.10.2026). Ролики
 * Астаны z_tim выкладывает сам без приёмки — они пролетают доску за минуты, и
 * Жулдыз их не замечала: «Выполнено» сворачивалось до четырёх.
 */
const doneAt = (t: Task) => Math.max(t.approvedAt ?? 0, ...t.items.map((i) => i.publishedAt ?? i.approvedAt ?? 0));

function sortCol(status: Status, list: Task[], moderator: boolean) {
  const s = [...list];
  if (status === "wait") s.sort((a, b) => Number(!!b.proposal && b.creator === ME_CLIENT) - Number(!!a.proposal && a.creator === ME_CLIENT) || b.createdAt - a.createdAt);
  if (status === "work" || status === "rework") s.sort((a, b) => a.due - b.due);
  if (status === "review")
    s.sort(
      (a, b) =>
        Number(b.creator === ME_CLIENT) - Number(a.creator === ME_CLIENT) ||
        (lastSubmitAt(b) - lastSubmitAt(a)),
    );
  // Первыми — те, что ждут ссылок дольше всех: забывают именно их.
  if (status === "publish") s.sort((a, b) => (oldestApprovedAt(a) - oldestApprovedAt(b)));
  if (status === "done") s.sort((a, b) => doneAt(b) - doneAt(a));
  // Модератору наверх — то, что он ещё не смотрел
  if (moderator) return s.sort((a, b) => Number(needsCheck(b)) - Number(needsCheck(a)));
  // Мои задачи — наверху колонки (сортировка устойчивая, порядок внутри групп сохраняется)
  return s.sort((a, b) => Number(b.creator === ME_CLIENT) - Number(a.creator === ME_CLIENT));
}

/**
 * «Публикация» по дням принятия (23.10.2026): ролики без ссылки копились
 * одним длинным столбцом, и старые тонули под свежими. Свежие — группами
 * «Сегодня / Вчера / 2–3 дня», а всё, что ждёт ссылку дольше трёх дней, —
 * одной красной плашкой сверху: видно сразу, раскрывается по нажатию.
 */
export const PUBLISH_GROUPS = ["Сегодня", "Вчера", "2–3 дня"] as const;

export function publishAge(t: Task, today: number): number {
  const at = oldestApprovedAt(t);
  return at === Number.MAX_SAFE_INTEGER ? 0 : Math.max(0, today - dayOf(at));
}

function PublishColumn({ list, now, moderator, onOpen }: { list: Task[]; now: number; moderator: boolean; onOpen: (t: Task) => void }) {
  const [oldOpen, setOldOpen] = useState(false);
  const today = dayOf(now);
  const old = list.filter((t) => publishAge(t, today) > 3);
  const groups = PUBLISH_GROUPS.map((name, i) => ({
    name,
    tasks: list.filter((t) => {
      const age = publishAge(t, today);
      return i === 2 ? age >= 2 && age <= 3 : age === i;
    }),
  })).filter((g) => g.tasks.length);
  const card = (t: Task) => <TaskCard key={t.id} task={t} now={now} me={ME_CLIENT} moderator={moderator} onOpen={() => onOpen(t)} />;
  return (
    <>
      {old.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setOldOpen((o) => !o)}
            className="flex shrink-0 items-center justify-between gap-1.5 rounded-[14px] bg-rework-soft px-3 py-2 text-left text-[13px] font-medium text-rework-fg"
          >
            <span>{old.length} ждут ссылку больше 3 дней</span>
            <motion.span animate={{ rotate: oldOpen ? 180 : 0 }} transition={spring.snappy}>
              <ChevronDown className="size-3.5" />
            </motion.span>
          </button>
          {oldOpen && old.map(card)}
        </>
      )}
      {groups.map((g) => (
        <div key={g.name} className="flex flex-col gap-2">
          <div className="flex items-center gap-1.5 px-1 pt-1 text-xs font-medium text-muted-foreground">
            {g.name}
            <span className="tabular-nums">· {g.tasks.length}</span>
          </div>
          {g.tasks.map(card)}
        </div>
      ))}
    </>
  );
}

function Columns({ tasks }: { tasks: Task[] }) {
  const now = useNow();
  const doneExpanded = useStore((s) => s.doneExpanded);
  const toggleDone = useStore((s) => s.toggleDoneExpanded);
  const openTask = useStore((s) => s.openTask);
  const q = useStore((s) => s.q);
  const filtered = useStore((s) => Boolean(s.q || s.board.direction || s.board.editor));
  const moderator = useStore((s) => s.role) === "moderator";
  const myTurn = tasks.filter((t) => t.status === "review" && (moderator ? needsCheck(t) : isMyStaging(t))).length;
  return (
    <div className="no-scrollbar -mx-1 flex min-h-0 flex-1 gap-2.5 overflow-x-auto px-1">
      {COLS.map((col) => {
        const all = sortCol(col.status, tasks.filter((t) => t.status === col.status), moderator);
        const isReview = col.status === "review";
        const isDone = col.status === "done";
        // Вышедшее сегодня видно всё; раньше — свёрнуто (минимум четыре карточки).
        const today = dayOf(now);
        const doneToday = isDone ? all.filter((t) => dayOf(doneAt(t)) === today).length : 0;
        const shown = isDone && !doneExpanded ? all.slice(0, Math.max(4, doneToday)) : all;
        const hidden = all.length - shown.length;
        return (
          <section key={col.status} className={cn("flex min-w-[164px] flex-1 flex-col rounded-col p-2", isReview ? "bg-review-soft" : "bg-muted")}>
            <div className="flex h-9 items-center gap-2 px-2.5 pt-1">
              <span className={cn("size-2 rounded-full", col.dot, isReview && myTurn > 0 && "pulse-dot text-review")} />
              <span className="text-sm font-semibold">{col.name}</span>
              <CountPill>
                <Ticker value={all.length} />
              </CountPill>
            </div>
            <motion.div layoutScroll className="no-scrollbar flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden pb-16 pt-1.5">
              {col.status === "publish"
                ? <PublishColumn list={all} now={now} moderator={moderator} onOpen={(t) => openTask(t.id, "task")} />
                : shown.map((t) => (
                  <TaskCard key={t.id} task={t} now={now} me={ME_CLIENT} moderator={moderator} onOpen={() => openTask(t.id, "task")} />
                ))}
              {all.length === 0 && (
                <div className="grid h-20 place-items-center rounded-[20px] border border-dashed border-ring/60 text-xs text-muted-foreground">{filtered ? "Ничего не нашлось" : "Пусто"}</div>
              )}
              {isDone && (hidden > 0 || doneExpanded) && (
                <button
                  type="button"
                  onClick={toggleDone}
                  className="flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-full text-[13px] font-medium text-muted-foreground hover:bg-background"
                >
                  {doneExpanded ? "Свернуть" : `Ещё ${hidden} за неделю`}
                  <motion.span animate={{ rotate: doneExpanded ? 180 : 0 }} transition={spring.snappy}>
                    <ChevronDown className="size-3.5" />
                  </motion.span>
                </button>
              )}
            </motion.div>
          </section>
        );
      })}
    </div>
  );
}

const REMEMBER_DIRECTION = "montage:board:direction";
const REMEMBER_EDITOR = "montage:board:editor";

/**
 * Фильтры доски в адресе (`?direction=&editor=`) и в «последнем выборе»
 * (02.11.2026): Жулдыз смотрит одно направление, и после каждого захода
 * выбирать его заново — лишнее. Источник — стор: его читает и окно постановки
 * («Показать», если новая карточка спрятана фильтром).
 */
function useBoardFilterUrl() {
  const [params, setParams] = useSafeSearchParams();
  const board = useStore((s) => s.board);
  const setBoard = useStore((s) => s.setBoard);
  const ready = useRef(false);

  // Вход на доску: адрес главнее запомненного.
  useEffect(() => {
    const direction = params.get("direction") ?? readRemembered<string | null>(REMEMBER_DIRECTION, null);
    const editor = params.get("editor") ?? readRemembered<string | null>(REMEMBER_EDITOR, null);
    setBoard({ direction: direction && PROJECTS.some((p) => p.id === direction) ? direction : null, editor: editor || null });
    // Только при входе: дальше ведёт стор.
  }, []);

  useEffect(() => {
    // Первый проход — ещё со старым стором: не затираем им запомненное.
    if (!ready.current) {
      ready.current = true;
      return;
    }
    writeRemembered(REMEMBER_DIRECTION, board.direction);
    writeRemembered(REMEMBER_EDITOR, board.editor);
    if ((params.get("direction") ?? null) === board.direction && (params.get("editor") ?? null) === board.editor) return;
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (board.direction) next.set("direction", board.direction);
      else next.delete("direction");
      if (board.editor) next.set("editor", board.editor);
      else next.delete("editor");
      return next;
    }, { replace: true });
  }, [board.direction, board.editor]);
}

/** Чип-список: «Направление · Все ▾». */
function FilterPick({ label, value, options, onChange }: { label: string; value: string | null; options: { id: string; name: string }[]; onChange: (id: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.id === value);
  if (options.length < 2 && !current) return null;
  const pick = (id: string | null) => {
    onChange(id);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border pl-3.5 pr-2.5 text-[13px] font-medium shadow-mxs transition-colors",
            current ? "border-primary bg-primary text-primary-foreground" : "bg-background hover:bg-muted",
          )}
        >
          <span className={cn(!current && "text-muted-foreground")}>{label}</span>
          {current && <span className="max-w-[140px] truncate">{current.name}</span>}
          <ChevronDown className="size-3.5 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="montage-root w-64 rounded-[18px] p-1.5" align="start">
        <div className="flex max-h-80 flex-col overflow-y-auto">
          {[{ id: null as string | null, name: "Все" }, ...options].map((o) => (
            <button
              key={o.id ?? "all"}
              type="button"
              onClick={() => pick(o.id)}
              className="flex items-center gap-2 rounded-[12px] px-3 py-1.5 text-left text-[13px] hover:bg-muted"
            >
              <Check className={cn("size-3.5 shrink-0", o.id === value ? "opacity-100" : "opacity-0")} />
              <span className="truncate">{o.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Раздел «Монтаж»: сверху мои задачи монтажёрам, ниже — общая воронка или календарь команды */
export function Montage({ view }: { view: "board" | "calendar" }) {
  const now = useNow();
  const tasks = useStore((s) => s.tasks);
  const busy = useStore((s) => s.busy);
  const q = useStore((s) => s.q);
  const setQ = useStore((s) => s.setQ);
  const navigate = useStore((s) => s.navigate);
  const openTask = useStore((s) => s.openTask);
  const openNewTask = useStore((s) => s.openNewTask);
  // Модератор смотрит ту же доску отдела: без постановки задач и без календаря монтажёров
  const moderator = useStore((s) => s.role) === "moderator";
  const [searchOpen, setSearchOpen] = useState(q.length > 0);
  const [calDept, setCalDept] = useState<Dept>(PEOPLE[ME_CLIENT].dept ?? DEPTS[0] ?? "");
  const board = useStore((s) => s.board);
  const setBoard = useStore((s) => s.setBoard);
  useBoardFilterUrl();

  // Чипы: направления и монтажёры, которые есть на доске (и выбранный, даже если пропал).
  const directions = useMemo(() => {
    const ids = new Set(tasks.map((t) => t.projectId).filter(Boolean) as string[]);
    if (board.direction) ids.add(board.direction);
    return PROJECTS.filter((p) => ids.has(p.id)).map((p) => ({ id: p.id, name: p.name }));
  }, [tasks, board.direction]);
  const editors = useMemo(() => {
    const ids = new Set(tasks.map((t) => t.editor).filter(Boolean));
    if (board.editor) ids.add(board.editor);
    return [...ids].map((id) => ({ id, name: PEOPLE[id].name })).sort((a, b) => a.name.localeCompare(b.name, "ru"));
  }, [tasks, board.editor]);

  const visible = useMemo(
    () => tasks.filter((t) =>
      (!q || t.title.toLowerCase().includes(q.toLowerCase()))
      && (!board.direction || t.projectId === board.direction)
      && (!board.editor || t.editor === board.editor)),
    [tasks, q, board.direction, board.editor],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-5">
      {!moderator && <PushPrompt />}
      <header className="flex items-center justify-between gap-6">
        <div className="flex min-w-0 items-center gap-4">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.h1
              key={view}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: easeOut }}
              className="sr-only"
            >
              {moderator ? "Модерация" : view === "board" ? "Монтаж" : "Календарь"}
            </motion.h1>
          </AnimatePresence>
          <div className={cn(moderator && "hidden")}>
            <Segmented
              id="view"
              value={view}
              onChange={(v) => navigate({ name: v === "board" ? "board" : "clientCalendar" })}
              options={[
                { value: "board", label: "Доска", icon: <SquareKanban /> },
                { value: "calendar", label: "Календарь", icon: <CalendarDays /> },
              ]}
            />
          </div>
          {view === "board" && (
            <>
              <FilterPick label="Направление" value={board.direction} options={directions} onChange={(direction) => setBoard({ direction })} />
              <FilterPick label="Монтажёр" value={board.editor} options={editors} onChange={(editor) => setBoard({ editor })} />
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          {view === "board" && (
            // Анимируем ширину, а не layout-трансформ — иначе иконка растягивается при сворачивании
            <motion.div
              initial={false}
              animate={{ width: searchOpen ? 240 : 36 }}
              transition={spring.snappy}
              className="flex h-9 shrink-0 items-center overflow-hidden rounded-full border bg-background shadow-mxs"
            >
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                tabIndex={searchOpen ? -1 : 0}
                className="grid size-[34px] shrink-0 place-items-center"
                title="Поиск"
              >
                <Search className="size-4" />
              </button>
              <AnimatePresence initial={false}>
                {searchOpen && (
                  <motion.div
                    key="field"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { delay: 0.05, duration: 0.15 } }}
                    exit={{ opacity: 0, transition: { duration: 0.08 } }}
                    className="flex min-w-0 flex-1 items-center pr-1"
                  >
                    <input
                      autoFocus
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Найти публикацию…"
                      className="min-w-0 flex-1 bg-transparent pr-2 text-[13px] outline-none placeholder:text-muted-foreground"
                      onKeyDown={(e) => e.key === "Escape" && (setQ(""), setSearchOpen(false))}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setQ("");
                        setSearchOpen(false);
                      }}
                      className="grid size-7 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-muted"
                      title="Закрыть поиск"
                    >
                      <X className="size-3.5" />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
          {!moderator && (
            <Button onClick={() => openNewTask({ mode: "create" })}>
              <Plus />
              Новая публикация
            </Button>
          )}
        </div>
      </header>

      <AnimatePresence mode="wait" initial={false}>
        {view === "board" ? (
          <motion.div
            key="board"
            className="flex min-h-0 flex-1"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: easeOut }}
          >
            <Columns tasks={visible} />
          </motion.div>
        ) : (
          <motion.div
            key="calendar"
            className="scroll-thin min-h-0 flex-1 overflow-y-auto pb-4"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: easeOut }}
          >
            <DayByPeople
              editors={editorsOf(calDept)}
              dept={calDept}
              onDept={setCalDept}
              now={now}
              tasks={tasks}
              busy={busy}
              onTaskClick={(t) => openTask(t.id, "cal")}
              onAdd={(editor, day) => openNewTask({ mode: "create", preset: { editor, day } })}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
